/**
 * iter-72 — Synth domain module extracted from /lib/audioEngine.js
 *
 * Three exports cover the MIDI / synth concern:
 *   • `scheduleSynthNote(audioCtx, dest, pitch, startTime, duration, velocity, waveform)`
 *     — schedules a single polysynth voice (3 detuned saws + sub octave) at
 *     the given AudioContext time. Works for BOTH online (live preview) and
 *     offline (renderMidiClipToBuffer) AudioContexts. Pure function: takes
 *     the ctx + destination as arguments so callers control routing.
 *
 *   • `previewMidiNote(ctx, pitch, opts)` — convenience wrapper that schedules
 *     a note via the LIVE running AudioContext routed directly to
 *     `ctx.destination`. Auto-resumes a suspended context (browser autoplay
 *     guard). Used by the MIDI Clip Editor piano-key preview.
 *
 *   • `renderMidiClipToBuffer(srcSampleRate, clip)` — renders an entire MIDI
 *     clip into an offline AudioContext and returns the resulting
 *     AudioBuffer. Used to bake a generated MIDI clip into something
 *     loadBufferToChannel can consume.
 *
 * Public surface is preserved by audioEngine.js — `engine.previewMidiNote(...)`
 * and `engine.renderMidiClipToBuffer(...)` still work; they now delegate here.
 */

/** MIDI synth voice — schedule a single pitched note via a 3-osc + envelope
 *  chain. Used by the MIDI Clip Editor preview button and (offline) by the
 *  buffer renderer below. Routes directly to `dest` so the caller controls
 *  routing (online: ctx.destination; offline: offline.destination).
 *
 *  - waveform: "sawtooth" gives a bright lead tone; "triangle" is mellower.
 *  - We mix 3 detuned saws + a sub-octave sine for body, like a classic
 *    polysynth voice. ADSR is fast-attack / medium-release so chord
 *    voices don't smear into one another.
 */
export function scheduleSynthNote(audioCtx, dest, pitch, startTime, duration, velocity = 100, waveform = "sawtooth") {
  const freq = 440 * Math.pow(2, (pitch - 69) / 12);
  const v = Math.max(0, Math.min(1, velocity / 127));
  const peak = 0.18 * v;  // headroom — many notes can stack

  const out = audioCtx.createGain();
  out.gain.setValueAtTime(0, startTime);
  out.gain.linearRampToValueAtTime(peak, startTime + 0.005);
  out.gain.setValueAtTime(peak, startTime + Math.max(0.005, duration - 0.04));
  out.gain.exponentialRampToValueAtTime(0.0001, startTime + duration + 0.05);
  out.connect(dest);

  // Three detuned voices for a fat polysynth lead.
  const detunes = [-7, 0, 7];
  detunes.forEach((cents) => {
    const o = audioCtx.createOscillator();
    o.type = waveform;
    o.frequency.value = freq;
    o.detune.value = cents;
    o.connect(out);
    o.start(startTime);
    o.stop(startTime + duration + 0.06);
  });
  // Sub-octave sine for low-end body.
  const sub = audioCtx.createOscillator();
  sub.type = "sine";
  sub.frequency.value = freq / 2;
  const subGain = audioCtx.createGain();
  subGain.gain.value = 0.4;
  sub.connect(subGain);
  subGain.connect(out);
  sub.start(startTime);
  sub.stop(startTime + duration + 0.06);
}

/** Live-preview a MIDI note through the engine's running AudioContext.
 *  Auto-resumes a suspended context (browsers gate audio until first user
 *  gesture). No-op if `ctx` is null. */
export function previewMidiNote(ctx, pitch, duration = 0.4, velocity = 100, waveform = "sawtooth") {
  if (!ctx) return;
  if (ctx.state === "suspended") {
    try { ctx.resume(); } catch (_e) { void _e; }
  }
  scheduleSynthNote(ctx, ctx.destination, pitch, ctx.currentTime + 0.005, duration, velocity, waveform);
}

/** Render a MIDI clip to a WAV-compatible AudioBuffer via OfflineAudioContext.
 *  Returns the rendered buffer, ready to load into a channel.
 *
 *  `clip = { notes: [{pitch, startBeat, durationBeats, velocity}], tempo, beatsPerBar, bars, waveform? }`
 *
 *  beat ↔ seconds conversion uses `tempo` (BPM); total duration is
 *  `bars * beatsPerBar / tempo * 60` seconds. We add a 0.2s tail so the
 *  release envelope of the last note doesn't clip. */
export async function renderMidiClipToBuffer(srcSampleRate, clip) {
  const tempo = clip.tempo || 120;
  const beatsPerBar = clip.beatsPerBar || 4;
  const bars = clip.bars || 4;
  const waveform = clip.waveform || "sawtooth";
  const secPerBeat = 60 / tempo;
  const lengthSec = bars * beatsPerBar * secPerBeat + 0.2;
  const sampleRate = srcSampleRate || 44100;
  const channels = 2;
  const offline = new OfflineAudioContext(channels, Math.ceil(sampleRate * lengthSec), sampleRate);
  const out = offline.createGain();
  out.gain.value = 1.0;
  out.connect(offline.destination);
  (clip.notes || []).forEach((n) => {
    const start = n.startBeat * secPerBeat;
    const dur = Math.max(0.02, n.durationBeats * secPerBeat);
    scheduleSynthNote(offline, out, n.pitch, start, dur, n.velocity ?? 100, waveform);
  });
  return await offline.startRendering();
}
